// PascalCase = convention Compose pour les fonctions @Composable (cf. HarvestDetailScreen.kt).
@file:Suppress("ktlint:standard:function-naming")

package fr.frux.feature.harvestdetail

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.Place
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.label
import fr.frux.feature.harvestdetail.resources.Res
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_hosted_body
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_hosted_body_anonymous
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_hosted_title
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_rule_ask
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_rule_open
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_rule_private
import fr.frux.feature.harvestdetail.resources.harvestdetail_site_unavailable
import fr.frux.feature.harvestdetail.resources.harvestdetail_tree_unavailable
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.presentation.toFruxStatus
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * Encart « Chez moi, je vous invite » (ADR 0035, artboard `FichePriveRecolte`) : le propriétaire
 * du lieu accueille lui-même, ce qui explique à un inconnu pourquoi un lieu privé s'ouvre.
 */
@Composable
internal fun HarvestSiteCard(
    harvest: Harvest,
    modifier: Modifier = Modifier,
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        colors =
            CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.tertiaryContainer,
                contentColor = MaterialTheme.colorScheme.onTertiaryContainer,
            ),
    ) {
        Column(
            modifier = Modifier.padding(FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.Home, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(FruxSpacing.xs))
                Text(
                    text = stringResource(Res.string.harvestdetail_site_hosted_title),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
            val organizer = harvest.createdByUsername
            Text(
                text =
                    if (organizer != null) {
                        stringResource(Res.string.harvestdetail_site_hosted_body, organizer)
                    } else {
                        stringResource(Res.string.harvestdetail_site_hosted_body_anonymous)
                    },
                style = MaterialTheme.typography.bodyMedium,
            )
        }
    }
}

/** Ligne de la cible de la récolte dans la carte des faits : le lieu s'il y en a un, l'arbre sinon. */
@Composable
internal fun HarvestTargetFact(
    harvest: Harvest,
    tree: Tree?,
    site: Site?,
    onTreeClick: () -> Unit,
    onSiteClick: () -> Unit,
) {
    when {
        harvest.siteId != null -> {
            HarvestSiteFactRow(harvest = harvest, site = site, onClick = onSiteClick)
        }

        tree != null -> {
            FactRow(
                icon = Icons.Outlined.Park,
                text = "${tree.species.commonNameFr} · ${tree.status.toFruxStatus().label}",
                onClick = onTreeClick,
            )
        }

        else -> {
            FactRow(icon = Icons.Outlined.Park, text = stringResource(Res.string.harvestdetail_tree_unavailable))
        }
    }
}

/** Ligne lieu de la carte des faits : « nom · règle d'accès », cliquable vers la fiche du lieu. */
@Composable
internal fun HarvestSiteFactRow(
    harvest: Harvest,
    site: Site?,
    onClick: () -> Unit,
) {
    val name = site?.name ?: harvest.siteName
    if (name == null) {
        FactRow(icon = Icons.Outlined.Place, text = stringResource(Res.string.harvestdetail_site_unavailable))
        return
    }
    val text =
        site?.let { "$name · ${stringResource(it.accessRule.labelRes())}" } ?: name
    FactRow(icon = Icons.Outlined.Place, text = text, onClick = onClick)
}

private fun SiteAccessRule.labelRes(): StringResource =
    when (this) {
        SiteAccessRule.OPEN -> Res.string.harvestdetail_site_rule_open
        SiteAccessRule.ASK -> Res.string.harvestdetail_site_rule_ask
        SiteAccessRule.PRIVATE -> Res.string.harvestdetail_site_rule_private
    }
